import Data from 'data';
import { d2_addpercent, skillAuraStats, skillCalc, skillFormula, skillPassiveStats } from 'logic/skills/calc';
import skillFunctions from 'logic/skills/functions';
import { monsterFixItems, monsterHealth, selectMonster } from './picker';
import monsterStats from './stats';

export function defaultSummon(id, stats, level, target, extra) {
  const skill = Data.skills[id];
  if (!skill.summon) return;

  const {stats: extraStats={}, ...extraOther} = extra || {};

  extraStats.skill = {
    ...skillAuraStats(skill, stats, level),
    ...skillPassiveStats(skill, stats, level),
    ...extraStats.skill,
  };
  if (skill.aurastate) {
    extraStats.skill[`state#${skill.aurastate}`] = 1;
  }
  if (extraStats.skill.maxhp) {
    extraOther.life = (extraOther.life || 0) + (extraStats.skill.maxhp >> 8);
    delete extraStats.skill.maxhp;
  }

  const sumSkills = {};
  for (let i = 1; i <= 5; ++i) {
    if (skill[`sumskill${i}`]) {
      sumSkills[skill[`sumskill${i}`]] = skillCalc(skill, skill[`sumsk${i}calc`], stats, level);
    }
  }

  let group = skill.pettype;
  if (!Data.info.patch) {
    if (group === 'spiritwolf' || group === 'fenris' || group === 'grizzly') {
      group = 'druidanimal';
    }
  }

  const info = Data.monsters[skill.summon.toLowerCase()];
  if (info?.skilldamage) {
    const skill = Data.skills[info.skilldamage];
    const base = extraStats.base = {};
    base.mindamage = skillFormula(skill, 'pdmn', stats, level);
    base.maxdamage = skillFormula(skill, 'pdmx', stats, level);
    base.tohit = skillFormula(skill, 'toht', stats, level);
    switch (skill.etype) {
    case 'fire':
      base.firemindam = skillFormula(skill, 'edmn', stats, level);
      base.firemaxdam = skillFormula(skill, 'edmx', stats, level);
      break;
    case 'cold':
      base.coldmindam = skillFormula(skill, 'edmn', stats, level);
      base.coldmaxdam = skillFormula(skill, 'edmx', stats, level);
      base.coldlength = skillFormula(skill, 'edln', stats, level);
      break;
    case 'ltng':
      base.lightmindam = skillFormula(skill, 'edmn', stats, level);
      base.lightmaxdam = skillFormula(skill, 'edmx', stats, level);
      break;
    case 'pois':
      base.poisonmindam = skillFormula(skill, 'edmn', stats, level);
      base.poisonmaxdam = skillFormula(skill, 'edmx', stats, level);
      base.poisonlength = skillFormula(skill, 'edln', stats, level);
      base.poison_count = 1;
      break;
    case 'mag':
      base.magicmindam = skillFormula(skill, 'edmn', stats, level);
      base.magicmaxdam = skillFormula(skill, 'edmx', stats, level);
      break;
    // no default
    }
  }

  return {
    monster: skill.summon.toLowerCase(),
    level: stats.level,
    group,

    skill: id,
    skillLevel: level,

    life_percent: skillFormula(skill, 'clc1', stats, level),
    stats: extraStats,
    skills: sumSkills,

    ...extraOther,
  };
}

export function createSummon(state, stats, id, target, level) {
  const skill = Data.skills[id];
  if (!skill) return state;
  const info = skillFunctions[skill.srvdofunc || 0]?.summon?.(id, stats.player, level, target);
  if (!info) return state;

  state = {...state};
  const summons = state.summons = {...state.summons};
  if (info.group) {
    for (const id in summons) {
      if (summons[id].group === info.group) {
        delete summons[id];
      }
    }
  }
  const profile = state.profiles[state.activeProfile];
  if (info.revive) {
    const monster = {...selectMonster(profile)};
    const healthInit = monsterHealth(monster, profile.difficulty, profile.level);
    monster.players = 1;
    monster.level = Math.min(target.level, info.level);
    monster.level_ac = target.level;
    const health = monsterHealth(monster, profile.difficulty, profile.level);
    monster.maxhp = monster.maxhp * health.max / healthInit.max;
    if (info.level < target.level) {
      monster.maxhp *= info.level / target.level;
    }
    monster.maxhp = d2_addpercent(monster.maxhp | 0, info.life_percent);

    monster.stats = info.stats;
    monster.skill = info.skill;
    monster.skillLevel = info.skillLevel;
    monster.revive = true;
    monster.unit = `revive_${target.class}`;

    summons[monster.unit] = monster;
  } else {
    summons[info.monster] = info;

    if (info.itemLevel) {
      const monster = monsterFixItems({id: info.monster}, info.itemLevel, info.skillLevel);
      info.items = monster.items;
    }
  }

  return state;
}

function summonRevive(summon, profile, state) {
  const stats = monsterStats(summon, profile.difficulty, profile.level);
  stats.unit = summon.unit;
  stats.team = 0;
  stats.revive = true;
  return stats;
}

export function summonStats(summon, profile, state) {
  if (summon.revive) return summonRevive(summon, profile, state);

  const monster = {
    single: profile.monster?.single,
    id: summon.monster,
    type: 'summon',
    level: summon.level,
    items: summon.items,
    maxhp: 0,
    attack: 'a1',
    players: 1,
  };
  if (summon.monster === 'irongolem') {
    const item = state.items[profile.ironGolem];
    const type = Data.itemTypes[Data.items[item?.base]?.type];
    if (type) {
      monster.items = {[type.bodyloc1]: profile.ironGolem};
    }
  }
  const health = monsterHealth(monster, profile.difficulty, profile.level);
  monster.maxhp = Math.round((health.min + health.max) / 2) + (summon.life || 0);
  if (summon.life_percent) monster.maxhp = d2_addpercent(monster.maxhp, summon.life_percent);
  const stats = monsterStats(monster, profile.difficulty, profile.level);
  stats.unit = summon.group || summon.monster;
  stats.team = 0;
  stats.untargetable = summon.untargetable;

  const {base: baseStats, ...otherStats} = summon.stats;
  if (baseStats) {
    for (const id in baseStats) {
      stats.stats.base[id] = (stats.stats.base[id] || 0) + baseStats[id];
    }
  }
  Object.assign(stats.stats, otherStats);
  Object.assign(stats.skills, summon.skills);

  return stats;
}
